// Sentry init — gated on REACT_APP_SENTRY_DSN.
// Without the env var Sentry stays off (no calls to Sentry.* do anything).
//
// Setup:
//   1. Create a Sentry project at sentry.io
//   2. Copy the project DSN
//   3. Add to .env.local (dev) and your hosting env (prod):
//        REACT_APP_SENTRY_DSN=https://<key>@<id>.ingest.sentry.io/<project>
//   4. Optional: REACT_APP_SENTRY_ENV=production|staging
//
// In hot paths we explicitly avoid touching Sentry unless it was actually
// initialised, so the `if (!isInitialised)` checks in helpers below are real.

import * as Sentry from '@sentry/react';

let isInitialised = false;

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn || isInitialised) return;
  try {
    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || 'development',
      // Reasonable defaults — adjust sampling later if it gets noisy/expensive.
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      // Don't ship PII unless the user actually IDs themselves. Names/emails are
      // attached deliberately via setSentryUser below.
      sendDefaultPii: false
    });
    isInitialised = true;
  } catch (e) {
    // Never let Sentry init crash the app
    console.warn('Sentry init failed:', e);
  }
}

export function setSentryUser(user) {
  if (!isInitialised) return;
  try {
    if (!user) { Sentry.setUser(null); return; }
    Sentry.setUser({ id: user.uid, email: user.email || undefined });
  } catch {
    // ignore
  }
}

export function captureError(err, context) {
  if (!isInitialised) return;
  try {
    Sentry.captureException(err, context ? { extra: context } : undefined);
  } catch {
    // ignore
  }
}

export const SentryReact = Sentry;
export default Sentry;
